import type { Attribution, AuthMethod, UTMParams } from "types/analytics";
import { ICP_TYPE_PARAM, UTM_KEYS } from "constants/analytics";

const PROVIDER_TO_AUTH_METHOD: Partial<Record<string, AuthMethod>> = {
  cognito: "email",
};

/** Maps the internal Refine/Cognito provider value to the analytics AuthMethod. */
export const toAuthMethod = (provider: string): AuthMethod =>
  PROVIDER_TO_AUTH_METHOD[provider] ?? (provider as AuthMethod);

export const hasUTMValues = (params: UTMParams): boolean =>
  Object.values(params).some(Boolean);

// Kept in memory only: writing it to browser storage before cookie consent
// is not allowed. Lost on reload by design.
let attribution: Attribution = { utm: {} };

export const getAttribution = (): Attribution => attribution;

// First value wins: a later empty source never clears what we already have
const restoreAttribution = ({ utm, icpType }: Attribution) => {
  attribution = {
    utm: hasUTMValues(attribution.utm) ? attribution.utm : utm,
    icpType: attribution.icpType ?? icpType,
  };
};

// Same keys as the landing URL, so one parser reads both the URL and customState
const readAttribution = (read: (key: string) => unknown): Attribution => {
  const utm: UTMParams = {};
  for (const key of UTM_KEYS) {
    const value = read(key);
    if (typeof value === "string" && value) utm[key] = value;
  }
  const icpType = read(ICP_TYPE_PARAM);
  return {
    utm,
    icpType: typeof icpType === "string" && icpType ? icpType : undefined,
  };
};

export const restoreAttributionFromUrl = (search: string) => {
  const urlParams = new URLSearchParams(search);
  restoreAttribution(readAttribution((key) => urlParams.get(key)));
};

// Google/Apple sign-in leaves the page: carry attribution through Amplify customState.
// URI-encoded because Amplify's hex encoding garbles characters above U+00FF (–, ’, emoji)
export const serializeAttribution = (): string | undefined => {
  if (!hasUTMValues(attribution.utm) && !attribution.icpType) return undefined;
  return encodeURIComponent(
    JSON.stringify({
      ...attribution.utm,
      [ICP_TYPE_PARAM]: attribution.icpType,
    })
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const restoreSerializedAttribution = (value: string) => {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(value));
    if (!isRecord(parsed)) return;
    restoreAttribution(readAttribution((key) => parsed[key]));
  } catch {
    // Not our customState
  }
};
